/**
 * Resolves Nix flake attribute paths to /nix/store out-paths via
 * `nix path-info --offline <flakeRef>#<attr>`.
 *
 * Used by the MCP wiring path (and Phase 3+ connector/mount paths) to
 * turn `${recipe:<id>:<sub>}` markers into absolute store paths before
 * subprocess spawn.
 *
 * **Async and bounded since #454.** Recipe resolution runs during session build,
 * i.e. from the `session_init` command handler with the WebSocket already live.
 * The previous `spawnSync("nix", …)` had **no timeout**: `--offline` rules out a
 * network stall, but `nix path-info` takes the store's SQLite lock, so a
 * concurrent nix operation blocks it indefinitely — and a synchronous spawn
 * blocks the whole single-threaded `skaile serve` event loop while it waits.
 * The recipe-map `statSync`/`readFileSync` sat on the same path (and are re-read
 * on every call by design, to pick up a live restage).
 */
import { execFile } from "node:child_process";
import { readFile, stat } from "node:fs/promises";
import { promisify } from "node:util";
import {
  tryWithFsDeadline,
  validateAssetRecipeAttr,
  withFsDeadline,
} from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";

const execFileAsync = promisify(execFile);

/**
 * Wall-clock ceiling for one `nix path-info` invocation.
 *
 * `--offline` means the only unbounded wait is the store's SQLite lock held by a
 * concurrent nix operation. A recipe that has not resolved within this is not
 * going to; failing here surfaces `RecipeUnavailableError` to the requester
 * instead of stalling session build indefinitely.
 */
export const DEFAULT_NIX_TIMEOUT_MS = 30_000;

/** Env override for {@link DEFAULT_NIX_TIMEOUT_MS}, in milliseconds. */
export const NIX_TIMEOUT_ENV_VAR = "SKAILE_NIX_TIMEOUT_MS";

/** Resolve the effective `nix path-info` timeout, re-read per call. */
function nixTimeoutMs(): number {
  const raw = process.env[NIX_TIMEOUT_ENV_VAR];
  if (raw === undefined) return DEFAULT_NIX_TIMEOUT_MS;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_NIX_TIMEOUT_MS;
}

export class RecipeUnavailableError extends Error {
  constructor(
    public readonly flakeRef: string,
    public readonly attr: string,
    cause?: unknown,
  ) {
    super(
      `Recipe unavailable: ${flakeRef}#${attr}${cause instanceof Error ? ` - ${cause.message}` : ""}`,
    );
    this.name = "RecipeUnavailableError";
  }
}

/**
 * Per-resolve-pass cache. Created fresh by each call to
 * `loadMcpServerDeclarations` integration so values don't go stale across
 * sessions but multiple recipes within one pass dedupe.
 */
export type RecipeResolutionCache = Map<string, string>;

export function createRecipeCache(): RecipeResolutionCache {
  return new Map();
}

const DEFAULT_RECIPE_MAP_PATH = "/etc/skaile/mcps-out-paths.json";

/** Resolves the prebuilt attr->storePath map file path (env override). */
function recipeMapPath(): string {
  return process.env.SKAILE_NIX_RECIPE_MAP || DEFAULT_RECIPE_MAP_PATH;
}

interface RecipeMapMemo {
  path: string;
  mtimeMs: number;
  map: Record<string, unknown> | null;
}

let recipeMapMemo: RecipeMapMemo | null = null;

/**
 * The read currently in flight, keyed by `<path>:<mtimeMs>`.
 *
 * The memo alone cannot make "warn once per (path, mtime)" true: it is written
 * only *after* the read, so two concurrent callers both miss it, both parse the
 * same corrupt file, and both warn. Sharing the in-flight promise collapses
 * them into one read, one warning, one memo write.
 */
let recipeMapInFlight: { key: string; promise: Promise<Record<string, unknown> | null> } | null =
  null;

/** Test-only: clears the (path, mtime) recipe-map memo so module-level state does not leak between cases. */
export function __resetRecipeMapMemoForTest(): void {
  recipeMapMemo = null;
  recipeMapInFlight = null;
}

/**
 * Loads the prebuilt attr->storePath map, memoized by (file path + mtime).
 * Re-reads when the file's mtime changes so a live restage of
 * `/etc/skaile/mcps-out-paths.json` is picked up without a process restart.
 * Returns null (and falls through to nix) when the file is missing or its
 * JSON is malformed; a malformed map warns once per distinct (path, mtime)
 * version — re-reading the same corrupt file is served from memo without
 * re-warning, but a later corrupt version (new mtime) warns again.
 *
 * Async and deadline-bounded since #454 — the stat and read run on every call by
 * design (that is what makes a live restage visible), so they must never be the
 * thing that parks the event loop.
 */
async function loadRecipeMap(): Promise<Record<string, unknown> | null> {
  const path = recipeMapPath();

  const mtimeMs = await tryWithFsDeadline(
    `stat ${path}`,
    async () => (await stat(path)).mtimeMs,
    null,
  );
  if (mtimeMs === null) {
    // File missing (or unreadable within the deadline) — memo the absence keyed
    // to this path, fall through. We intentionally re-stat on every call (the
    // memo never short-circuits the stat above) so a map staged later is
    // detected and picked up live.
    recipeMapMemo = { path, mtimeMs: -1, map: null };
    return null;
  }

  if (recipeMapMemo && recipeMapMemo.path === path && recipeMapMemo.mtimeMs === mtimeMs) {
    // Same version already seen (valid or malformed); do not re-warn.
    return recipeMapMemo.map;
  }

  // Concurrent callers on the same version share one read (see recipeMapInFlight).
  const key = `${path}:${mtimeMs}`;
  if (recipeMapInFlight?.key === key) return recipeMapInFlight.promise;

  const promise = readRecipeMap(path, mtimeMs);
  recipeMapInFlight = { key, promise };
  try {
    return await promise;
  } finally {
    if (recipeMapInFlight?.key === key) recipeMapInFlight = null;
  }
}

/** Read + parse one version of the recipe map, warn on corruption, memo the result. */
async function readRecipeMap(
  path: string,
  mtimeMs: number,
): Promise<Record<string, unknown> | null> {
  let map: Record<string, unknown> | null = null;
  try {
    const parsed: unknown = JSON.parse(
      await withFsDeadline(`readFile ${path}`, () => readFile(path, "utf8")),
    );
    map = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch (err) {
    // Reached only on a NEW (path, mtime) — the memo short-circuits an unchanged
    // version, and concurrent callers on this version share this one read. So a
    // corrupt map warns once per distinct file version, and a later
    // valid->corrupt->fixed->corrupt sequence warns on each corrupt one.
    createLogger({ kind: "mcp", subkind: "recipe-resolve" }).warn(
      "recipe map malformed — falling back to nix path-info",
      { path, error: err instanceof Error ? err.message : String(err) },
    );
    map = null;
  }

  recipeMapMemo = { path, mtimeMs, map };
  return map;
}

/** A pluggable strategy mapping a flake/asset ref + attr to an absolute store path. */
export interface RecipeProducer {
  /**
   * Returns the resolved store path, or `null` to defer to the next producer.
   * A producer may instead THROW (e.g. `NixRecipeProducer` throws
   * `RecipeUnavailableError`) to signal a terminal failure — `resolveViaProducers`
   * does not catch mid-chain, so a throw skips any producers registered after it.
   * Put deferring producers (those that return `null` on a miss) BEFORE any
   * throwing producer in the chain.
   *
   * Async since #454 — every producer does I/O (a file read, a subprocess) that
   * must not block the event loop.
   */
  resolve(ref: string, attr: string): Promise<string | null>;
  readonly kind: string;
}

/** Content-addressed store-path map producer. Nix is one PRODUCER of this map,
 *  not the only one. Generic; no nix knowledge. */
export class MapRecipeProducer implements RecipeProducer {
  readonly kind = "map";
  constructor(
    private readonly load: () => Promise<Record<string, unknown> | null> = loadRecipeMap,
  ) {}
  async resolve(_ref: string, attr: string): Promise<string | null> {
    const map = await this.load();
    if (!map) return null;
    const hit = attr
      .split(".")
      .reduce<unknown>(
        (o, k) => (o && typeof o === "object" ? (o as Record<string, unknown>)[k] : undefined),
        map,
      );
    return typeof hit === "string" && hit.startsWith("/nix/store/") ? hit : null;
  }
}

/** Nix flake producer — locates/builds the closure via `nix path-info --offline`. */
export class NixRecipeProducer implements RecipeProducer {
  readonly kind = "nix";
  async resolve(ref: string, attr: string): Promise<string | null> {
    let stdout: string;
    try {
      // `killSignal: "SIGKILL"` is deliberate: a nix process blocked on the
      // store's SQLite lock can sit in an uninterruptible wait where SIGTERM is
      // never delivered, so a SIGTERM-based timeout would not actually end it.
      ({ stdout } = await execFileAsync("nix", ["path-info", "--offline", `${ref}#${attr}`], {
        encoding: "utf8",
        timeout: nixTimeoutMs(),
        killSignal: "SIGKILL",
      }));
    } catch (err) {
      // execFile rejects on spawn failure, non-zero exit, and timeout alike; the
      // error carries `code`/`signal`/`stderr` when the process actually ran.
      const e = err as NodeJS.ErrnoException & { code?: number | string; stderr?: string };
      const detail =
        typeof e.code === "number"
          ? new Error(`nix path-info exited ${e.code}: ${(e.stderr ?? "").trim()}`)
          : err;
      throw new RecipeUnavailableError(ref, attr, detail);
    }
    const outPath = stdout.trim().split("\n")[0];
    if (!outPath?.startsWith("/nix/store/")) {
      throw new RecipeUnavailableError(ref, attr, new Error(`Unexpected output: ${stdout}`));
    }
    return outPath;
  }
}

/** Default chain: content-addressed map first, then nix build. */
export function defaultRecipeProducers(): RecipeProducer[] {
  return [new MapRecipeProducer(), new NixRecipeProducer()];
}

/** Try each producer in order; cache the first non-null by `ref#attr`. */
export async function resolveViaProducers(
  ref: string,
  attr: string,
  cache: RecipeResolutionCache,
  producers: RecipeProducer[] = defaultRecipeProducers(),
): Promise<string> {
  const key = `${ref}#${attr}`;
  const cached = cache.get(key);
  if (cached) return cached;

  // Validate attr before it reaches the nix execFile arg — defense-in-depth
  // for Phase 3+ community catalogs where attr may be untrusted.
  try {
    validateAssetRecipeAttr(attr);
  } catch (err) {
    throw new RecipeUnavailableError(
      ref,
      attr,
      err instanceof Error ? err : new Error(String(err)),
    );
  }

  const log = createLogger({ kind: "mcp", subkind: "recipe-resolve" });
  log.debug("resolving recipe", { flakeRef: ref, attr });

  for (const producer of producers) {
    const out = await producer.resolve(ref, attr);
    if (out) {
      cache.set(key, out);
      log.info("resolved recipe", { flakeRef: ref, attr, outPath: out, producer: producer.kind });
      return out;
    }
  }
  throw new RecipeUnavailableError(ref, attr, new Error("no producer resolved the recipe"));
}

/**
 * Resolves a flake attribute to its /nix/store out-path.
 * Throws RecipeUnavailableError on missing closure / nix failure.
 *
 * Thin back-compat wrapper over the default producer chain (map → nix).
 */
export function resolveRecipePath(
  flakeRef: string,
  attr: string,
  cache: RecipeResolutionCache,
): Promise<string> {
  return resolveViaProducers(flakeRef, attr, cache);
}
